import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import {
  checkNonMoneyDataForTable,
  trimObject,
  dateTimeFormatter,
} from "utils/utils";
import { getPaperRollList } from "../model";
import s from "./paperRollRequestList.module.scss";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import moment from "moment";
import { searchErrorHandler } from "utils/formValidation";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import RequestStatusOptions from "utils/statusOptions/RequestStatusOptions";
import tools from "routes/tools";
import Datepicker from "components/Input/Datepicker/Datepicker";
import dayjs from "dayjs";
import TextMAS from "components/TextMAS/TextMAS";
import useRefresh from "utils/useRefresh/useRefresh";

const PAGE_LOCALE_KEY = "page.paperRoll.paperRollRequestList.";

const INPUT_FIELD_INIT_STATE = {
  id: "",
  merchantId: "",
  createdStartDate: null,
  createdEndDate: null,
  status: "",
  phone: "",
  trackingNumber: "",
};

const INPUT_FIELD_ID_OVERRIDE_STATE = {
  merchantId: "",
  createdStartDate: "",
  createdEndDate: "",
  status: "",
};

const ERROR_INIT_STATE = {
  merchantId: false,
  status: false,
  createdStartDate: false,
  invalidCreatedStartDate: false,
  createdEndDate: false,
  invalidCreatedEndDate: false,
  invalidCreatedDateRange: false,
  tooLargeCreatedDateRange: false,
};

const paperRollRequestList = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "paperRollRequestList";

  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const isInitRef = useRef(true);

  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState(
    INPUT_FIELD_INIT_STATE,
  );
  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);
  const [isIdInput, setIsIdInput] = useState(false);

  const [columnsArray, setColumnsArray] = useState([]);

  useRefresh({
    pagePath: path,
    preventRefresh: loading,
    redirectFn,
    effectFn: () => handleSearch(),
  });

  useEffect(() => {
    if (searchFieldInput.id !== "" && !isIdInput) {
      setIsIdInput(true);
      setSearchFieldInput({
        ...searchFieldInput,
        ...INPUT_FIELD_ID_OVERRIDE_STATE,
      });
    } else if (searchFieldInput.id === "" && isIdInput) {
      setIsIdInput(false);
      setSearchFieldInput(INPUT_FIELD_INIT_STATE);
    }
  }, [searchFieldInput.id]);

  useEffect(() => {
    let isOudated = false;
    const getIsOudated = () => isOudated;

    initialFetchList(getIsOudated);

    return () => {
      isOudated = true;
    };
  }, []);

  const initialFetchList = async (getIsOudated) => {
    setLoading(true);
    const params = trimObject({
      ...INPUT_FIELD_INIT_STATE,
    });
    const payload = {
      page: 0,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiResponse = await getPaperRollList(payload);
    if (getIsOudated && getIsOudated()) return;

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            isInitRef.current = false;
            setDatasource(responseBody);
            setCurrentPage(parseInt(responseBody.page));
            setPageSize(parseInt(responseBody.size));
            pageSizeInfoRef.current.currentpage = parseInt(responseBody.page);
            pageSizeInfoRef.current.pagesize = parseInt(responseBody.size);
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
          }
        }
      }
    }

    setSearch(false);
    setLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput(INPUT_FIELD_INIT_STATE);
  };

  const resetError = () => {
    setError(ERROR_INIT_STATE);
  };

  const handleReset = () => {
    isInitRef.current = true;
    setIsIdInput(false);
    resetInputField();
    resetError();
    initialFetchList();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      const { errorObj, hasError } = searchErrorHandler(searchFieldInput, [
        (input) =>
          input.createdStartDate &&
          !input.createdEndDate && {
            createdEndDate: true,
          },
        (input) =>
          moment(input.createdEndDate).isAfter(moment().endOf("day")) && {
            invalidCreatedEndDate: true,
          },
        (input) =>
          input.createdEndDate &&
          !input.createdStartDate && {
            createdStartDate: true,
          },
        (input) =>
          moment(input.createdStartDate).isBefore(moment("2022-01-01")) && {
            invalidCreatedStartDate: true,
          },
        (input) =>
          moment(input.createdStartDate).isAfter(input.createdEndDate) && {
            invalidCreatedDateRange: true,
          },
        (input) => {
          const startDate = moment(input.createdStartDate).startOf("day");
          const endDate = moment(input.createdEndDate).startOf("day");
          if (endDate.diff(startDate, "month", true) > 6) {
            return { tooLargeCreatedDateRange: true };
          }
        },
      ]);
      setError(errorObj);
      if (hasError) return;

      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({
      ...searchFieldInput,
    });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiResponse = await getPaperRollList(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            isInitRef.current = false;
            setDatasource(responseBody);
            setCurrentPage(parseInt(responseBody.page));
            setPageSize(parseInt(responseBody.size));
            pageSizeInfoRef.current.currentpage = parseInt(responseBody.page);
            pageSizeInfoRef.current.pagesize = parseInt(responseBody.size);
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
          }
        }
      }
    }
    setSearch(true);
    setSearching(false);
  };
  const textInputOnChange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const isInputDisabled = (field) => {
    if (field !== "id" && searchFieldInput.id !== "") return true;
  };

  const createdDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    let date = moment(rawDate).isValid()
      ? moment(rawDate)
      : e !== null
        ? ""
        : null;
    if (type === "start") {
      date = moment(rawDate).isValid()
        ? date.startOf("day").toISOString()
        : date;
      setError({
        ...error,
        createdStartDate: false,
        invalidCreatedDateRange: false,
      });
      setSearchFieldInput({ ...searchFieldInput, createdStartDate: date });
    } else if (type === "end") {
      date = moment(rawDate).isValid() ? date.endOf("day").toISOString() : date;
      setError({
        ...error,
        createdEndDate: false,
        invalidCreatedDateRange: false,
      });
      setSearchFieldInput({ ...searchFieldInput, createdEndDate: date });
    } else {
      return;
    }
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const dateFormatValidator = (dateValue) => {
    const isValidDateFormat = moment(dateValue).isValid();
    return isValidDateFormat;
  };

  const columns = [
    {
      key: "id",
      title: t(PAGE_LOCALE_KEY + "columnTitle.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "merchant",
      title: t(PAGE_LOCALE_KEY + "columnTitle.merchant"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "receiver",
      title: t(PAGE_LOCALE_KEY + "columnTitle.receiver"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "phone",
      title: t(PAGE_LOCALE_KEY + "columnTitle.phone"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "address",
      title: t(PAGE_LOCALE_KEY + "columnTitle.address"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },

    // {
    //   key: "email",
    //   title: t(PAGE_LOCALE_KEY + "columnTitle.email"),
    //   sorter: false,
    //   render: (data, key) => {
    //     return checkNonMoneyDataForTable(data, key);
    //   },
    // },
    {
      key: "rollCount",
      title: t(PAGE_LOCALE_KEY + "columnTitle.rollCount"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "shippingPaidBy",
      title: t(PAGE_LOCALE_KEY + "columnTitle.shippingPaidBy"),
      sorter: false,
      render: (data, key) => {
        console.log(data[key]);
        return data[key] ? t(PAGE_LOCALE_KEY + "columnContent.shippingPaidBy." + data[key].toLowerCase()) : "";
      },
    },
    {
      key: "deliveryTrackingNumber",
      title: t(PAGE_LOCALE_KEY + "columnTitle.deliveryTrackingNumber"),
      sorter: false,
      render: (data, key) => {
        console.log(data[key]);
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "salesName",
      title: t(PAGE_LOCALE_KEY + "columnTitle.salesName"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "status",
      title: t(PAGE_LOCALE_KEY + "columnTitle.status"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "created",
      title: t(PAGE_LOCALE_KEY + "columnTitle.created"),
      sorter: false,
      render: (data, key) => {
        return dateTimeFormatter(checkNonMoneyDataForTable(data, key));
      },
    },
    {
      key: "remarks",
      title: t(PAGE_LOCALE_KEY + "columnTitle.remarks"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "internalRemarks",
      title: t(PAGE_LOCALE_KEY + "columnTitle.internalRemarks"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "createdBy",
      title: t(PAGE_LOCALE_KEY + "columnTitle.createdBy"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "completedBy",
      title: t(PAGE_LOCALE_KEY + "columnTitle.completedBy"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "lastEmailSent",
      title: t(PAGE_LOCALE_KEY + "columnTitle.lastEmailSent"),
      sorter: false,
      render: (data, key) => {
        return (
          <div
            className={`${s["email-chip"]} ${
              s[!!data[key] ? "sent" : "not-sent"]
            }`}
          >
            <TextMAS color="#fff" fontWeight="semibold" fontSize="small">
              {!!data[key]
                ? t(PAGE_LOCALE_KEY + "columnContent.lastEmailSent.emailSent")
                : t(
                    PAGE_LOCALE_KEY +
                      "columnContent.lastEmailSent.emailNotSent",
                  )}
            </TextMAS>
          </div>
        );
      },
    },
    // {
    //   key: "updated",
    //   title: t(PAGE_LOCALE_KEY + "columnTitle.updated"),
    //   sorter: false,
    //   render: (data, key) => {
    //     return dateTimeFormatter(checkNonMoneyDataForTable(data, key));
    //   },
    // },

    // For future use if there is a need to add other actions except onClick for detail page.
    // {
    //   key: "action",
    //   title: t(PAGE_LOCALE_KEY + "columnTitle.action"),
    //   sorter: false,
    //   render: (data) => {
    //     return (
    //       <div className={s.actionBtnContainer}>
    //         <button
    //           onClick={() => {
    //             const path =
    //               "/tools/paperRoll/paperRollRequestCreate/paperRollRequestCreate";
    //             const tabName = tools.find((tab) => tab.path === path).tabName;
    //             const payload = {
    //               name: `${tabName} #${data.id}`,
    //               path: path,
    //               state: {
    //                 id: data.id,
    //                 createFlag: false,
    //               },
    //             };
    //             redirectFn("redirectPage", payload);
    //           }}
    //         >
    //           {t(PAGE_LOCALE_KEY + "columnTitle.actions.edit")}
    //         </button>
    //       </div>
    //     );
    //   },
    // },
  ].map((column, index) => ({ ...column, index: index }));

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 30, 40],
    };
  }, [currentPage, pageSize]);
  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId + "0"}>
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.id.label")}
            placeholder={t(PAGE_LOCALE_KEY + "search.id.placeholder")}
            value={searchFieldInput.id}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "id")}
            onfocusFn={() => textInputOnFocus("id")}
            onblurFn={() => textInputOnBlur("id")}
            sxParams={{ textFocus: focus.id }}
            disabled={isInputDisabled("id")}
          />

          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.merchantId.label")}
            value={searchFieldInput.merchantId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "merchantId")}
            onfocusFn={() => textInputOnFocus("merchantId")}
            onblurFn={() => textInputOnBlur("merchantId")}
            sxParams={{ textFocus: focus.merchantId }}
            disabled={isInputDisabled("merchantId")}
          />

          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.phone.label")}
            value={searchFieldInput.phone}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "phone")}
            onfocusFn={() => textInputOnFocus("phone")}
            onblurFn={() => textInputOnBlur("phone")}
            sxParams={{ textFocus: focus.phone }}
            disabled={isInputDisabled("phone")}
          />

          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.trackingNumber.label")}
            value={searchFieldInput.trackingNumber}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "trackingNumber")}
            onfocusFn={() => textInputOnFocus("trackingNumber")}
            onblurFn={() => textInputOnBlur("trackingNumber")}
            sxParams={{ textFocus: focus.trackingNumber }}
            disabled={isInputDisabled("trackingNumber")}
          />

          <Datepicker
            label={t(PAGE_LOCALE_KEY + "search.createdStartDate.label")}
            errorMessage={
              error.invalidCreatedDateRange
                ? t(PAGE_LOCALE_KEY + "search.createdStartDate.errorMessage.2")
                : error.tooLargeCreatedDateRange
                  ? t(
                      PAGE_LOCALE_KEY +
                        "search.createdStartDate.errorMessage.3",
                    )
                  : error.invalidCreatedStartDate
                    ? t(
                        PAGE_LOCALE_KEY +
                          "search.createdStartDate.errorMessage.4",
                      )
                    : t(
                        PAGE_LOCALE_KEY +
                          "search.createdStartDate.errorMessage.1",
                      )
            }
            type="start"
            value={searchFieldInput.createdStartDate}
            limitValue={searchFieldInput.createdEndDate || dayjs()}
            minDate={dayjs("2022-01-01")}
            onchangeFn={createdDateOnChange}
            onfocusFn={() => textInputOnFocus("createdStartDate")}
            onblurFn={() =>
              textInputOnBlur("createdStartDate", dateFormatValidator)
            }
            sxParams={{
              datepickerFocus: focus.createdStartDate,
              datepickerError:
                error.createdStartDate ||
                error.invalidCreatedStartDate ||
                error.invalidCreatedDateRange ||
                error.tooLargeCreatedDateRange,
            }}
            disabled={isInputDisabled("createdStartDate")}
          />
          <Datepicker
            label={t(PAGE_LOCALE_KEY + "search.createdEndDate.label")}
            errorMessage={
              error.invalidCreatedDateRange
                ? t(PAGE_LOCALE_KEY + "search.createdEndDate.errorMessage.2")
                : error.tooLargeCreatedDateRange
                  ? t(PAGE_LOCALE_KEY + "search.createdEndDate.errorMessage.3")
                  : error.invalidCreatedEndDate
                    ? t(
                        PAGE_LOCALE_KEY +
                          "search.createdEndDate.errorMessage.4",
                      )
                    : t(
                        PAGE_LOCALE_KEY +
                          "search.createdEndDate.errorMessage.1",
                      )
            }
            type="end"
            value={searchFieldInput.createdEndDate}
            limitValue={
              searchFieldInput.createdStartDate || dayjs("2022-01-01")
            }
            maxDate={dayjs()}
            onchangeFn={createdDateOnChange}
            onfocusFn={() => textInputOnFocus("createdEndDate")}
            onblurFn={() =>
              textInputOnBlur("createdEndDate", dateFormatValidator)
            }
            sxParams={{
              datepickerFocus: focus.createdEndDate,
              datepickerError:
                error.createdEndDate ||
                error.invalidCreatedEndDate ||
                error.invalidCreatedDateRange ||
                error.tooLargeCreatedDateRange,
            }}
            disabled={isInputDisabled("createdEndDate")}
          />

          <Selection
            label={t(PAGE_LOCALE_KEY + "search.status.label")}
            selectionValue={searchFieldInput.status}
            onchangeFn={(e) => textInputOnChange(e, "status")}
            onfocusFn={() => textInputOnFocus("status")}
            onblurFn={() => textInputOnBlur("status")}
            options={RequestStatusOptions("all")}
            sxParams={{ selectionFocus: focus.status }}
            disabled={isInputDisabled("status")}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  const navigateToDetail = (data) => {
    const path =
      "/tools/paperRoll/paperRollRequestCreate/paperRollRequestCreate";
    const tabName = tools.find((tab) => tab.path === path).tabName;
    const payload = {
      name: `${tabName} #${data.id}`,
      path: path,
      state: {
        id: data.id,
        createFlag: false,
      },
    };
    redirectFn("redirectPage", payload);
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <FunctionalButtonBar
          loading={loading}
          refreshBtn
          handleRefresh={handleSearch}
          createBtn
          handleCreate={() => {
            const path =
              "/tools/paperRoll/paperRollRequestCreate/paperRollRequestCreate";
            const payload = {
              name: "Create Paper Request",
              path: path,
            };
            redirectFn("redirectPage", payload);
          }}
          columnDisplayBtn
          columnDisplayProps={{
            columns,
            columnsArray,
            setColumnsArray,
            rememberPreference: "thermalpaper",
          }}
        />
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              navigateToDetail={navigateToDetail}
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}

          {searching && <Spin type={"search"} />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(paperRollRequestList);
